/**
 * Localized strings for the React Native app
 * Based on iOS Localizable.strings for consistency
 */

export const strings = {
  // ALERT
  alert: {
    openSafari: "Open in Safari",
    copyLink: "Copy link",
    warning: "Warning",
    error: "Error",
    cancel: "Cancel",
    accept: "OK",
    delete: "Delete",
    select: "Select",
    confirm: "Confirm",
    message: "Message",
    // Delete Account confirmation (iOS parity)
    deleteAccountTitle: "Are you sure you want to delete your account?",
    deleteAccountMessage: "This action cannot be undone.",
    deleteAccountAction: "Delete",
  },

  // HOME
  home: {
    email: {
      msg: "Leave us a message",
      title: "Email",
    },
    phone: {
      title: "Front Office",
    },
    section: {
      title: "How can we help you?",
    },
    faqs: {
      title: "FAQS",
    },
    upcomingEvents: {
      title: "Upcoming Events",
    },
    rsvpEvents: {
      title: "RSVP TO EVENTS",
    },
    amenities: {
      title: "Useful Information",
    },
    property: {
      info: "Property info",
      residentHandbook: "Resident Handbook",
    },
    
    // Home - Community Resources
    communityResources: {
      title: "My community resources",
      more: {
        title: "More resources",
        description: "See what else is out there",
      },
      detail: {
        info: {
          title: "INFORMATION",
        },
        website: {
          title: "WEBSITE",
        },
        files: {
          title: "FILES",
        },
      },
    },

    // Home - Community marketplace
    marketplace: {
      title: "Community marketplace",
      description: "Safe, local, and easy - right within your community.",
    },

    // Home - WiFi
    wifi: {
      title: "Wi-Fi network",
      description: "View your network and password",
      button: {
        connected: "Connected",
        disconnected: "Connect",
        connecting: "Connecting...",
      },
      info: {
        networkName: "Network Name",
        password: "Password",
        unit: "Unit",
      },
      support: {
        title: "Support",
        subtitle: "Need help with your WiFi connection?",
        text: "Text Support",
        email: "Email Support",
        website: "Support Website",
      },
      noInfo: {
        title: "Support",
        subtitle: "There appears to be no wifi information available for your unit.\n\nIf this seems incorrect, please contact the CBX support team.",
      },
      qrCode: {
        title: "WiFi QR Code",
      },
    },

    // Home - Actions
    actions: {
      payments: {
        title: "Make a payment",
        noBalance: "Great! You have no outstanding charges.",
        disabled: "Your building's leasing system is currently undergoing routine maintenance.",
      },
      bulletins: {
        noNew: "No new bulletins",
        newSingular: "1 new bulletin!",
        newPlural: (count: number) => `${count} new bulletins!`,
      },
      maintenance: "Request maintenance",
    },

    // Home - Property Info
    propertyInfo: {
      contactOffice: "Contact the office",
      residentHandbook: "Resident handbook",
      title: "Property info",
    },
    


    // Home - Contact Office
    contactOfficeDetails: {
      hours: "Office Hours",
      website: "Website",
      handbook: "Resident Handbook",
    },
  },

  // NOTIFICATIONS (Bulletins)
  notifications: {
    title: "Bulletins",
    empty: {
      title: "No new notifications",
      description: "You have no new notifications at the moment.",
    },
    section: {
      header: "Settings",
      set: "General Settings",
      not: "Notifications",
    },
    roommates: "Shared Information with Roommates",
    roommatesEmail: "Enable Email",
    roommatesPhone: "Enable Phone Number",
    options: {
      rent: "Rent reminders",
      maintenance: "Maintenance updates",
      community: "Community updates",
      allow: "Allow push notifications",
    },
    description: {
      alert: "Please go to Settings and enable push notifications for the Resio application to receive push notifications from this app.",
    },
    alertSetting: "Go To Settings",
    
    // Mobile notification modal/screen strings
    mobile: {
      modalTitle: "Notifications",
      screenTitle: "All notifications",
      seeAll: "See all notifications",
      loading: "Loading notifications...",
      empty: {
        title: "No new notifications",
        message: "You have no new notifications at the moment",
      },
      showMore: "Show more",
      showLess: "Show less",
      delete: {
        title: "Delete Notification",
        message: "Are you sure you want to delete this notification?",
        confirm: "Delete",
        cancel: "Cancel",
      },
      defaultTitle: "Notification",
    },
    
    // Notification settings strings
    settings: {
      title: "Notification settings",
      subtitle: "Stay in the loop",
      description: "Decide what alerts you want to receive",
      rentReminders: {
        label: "Rent reminders",
        description: "Get notified about upcoming rent payments and due dates",
      },
      maintenanceUpdates: {
        label: "Maintenance updates",
        description: "Stay informed about maintenance schedules and progress",
      },
      communityUpdates: {
        label: "Community updates",
        description: "Receive important news and updates from your community",
      },
      // Desktop notification settings
      newMessages: {
        label: "New messages",
        description: "Get notified about messages in chats you're part of",
      },
      upcomingEvents: {
        label: "Upcoming events",
        description: "Stay informed about events in your community",
      },
      rsvpReminders: {
        label: "RSVP reminders",
        description: "Receive reminders for events you've RSVP'd to",
      },
      newBulletins: {
        label: "New bulletins",
        description: "Get notified about important bulletin postings from your property",
      },
    },

    // Desktop notification strings
    desktop: {
      title: "Notifications",
      empty: "No notifications",
      placeholder: {
        title: "Select a notification",
        subtitle: "Choose an item from the list to view details",
      },
      seeAll: "See all notifications >",
      defaultTitle: "Notification",
      noNew: "No new notifications",
    },
  },

  // AUTOPAY
  autopay: {
    active: "Autopay active",
    inactive: "Autopay setup",
  },

  // LOGIN
  login: {
    header: {
      title: "Welcome",
      subtitle: "Enter your information below to get started, we will verify your credentials to get you started.",
      adminTitle: "Welcome to Resio Community Portal!",
      adminSubtitle: "Admin sign in",
    },
    field: {
      email: "Email",
      password: "Password",
      passwordPlaceholder: "Password",
    },
    button: {
      login: "Login",
      adminLogin: "Admin Sign In",
      signIn: "Sign in",
      forgot: "Forgotten Password?",
      signup: "Sign Up Now",
    },
    label: {
      welcome: "Enter your information below to access features.",
      welcomeTitle: "Welcome",
      signup: "Don't have an account?",
      adminMode: "👨‍💼 Admin Login",
      residentMode: "🏠 Resident Login",
    },
    unable: "Unable to login with the provided email",
  },

  // USER SELECTION
  userSelection: {
    header: {
      title: "Welcome to Resident Portal",
      subtitle: "All in one place for residents living at our properties",
    },
    section: {
      subtitle: "Let's confirm...",
      web: "If you're not a resident and want to connect with a resident, please create an account at our website",
    },
    resident: {
      title: "I'm a resident",
      subtitle: "I'd like to manage my account and invite people to make payments on my behalf.",
    },
    nonResident: {
      title: "I'm not a resident", 
      subtitle: "I'd like to make a payment on behalf of a resident.",
    },
    button: {
      continue: "Continue",
      web: "Open the website",
    },
  },

  // SIGNUP
  signup: {
    header: {
      title: "Welcome",
      subtitle: "Enter your information below to get started, we will verify your credentials to get you started.",
    },
    field: {
      name: "Full Name",
      email: "Email",
    },
    btn: {
      signup: "Next",
      login: "Log In",
    },
    lbl: {
      signup: "Already have an account?",
    },
    duplicate: {
      account: "There is already an account registered with that email address.",
    },
    unable: "Unable to sign up with the provided email",
  },

  // LOGOUT
  logout: {
    question: "Do you really want to logout?",
    button: "Logout",
    desktop: {
      title: "Sign out",
      message: "Are you sure you want to sign out?",
      confirmText: "Yes, I'm sure",
      cancelText: "No, cancel",
    },
  },

  // EVENTS
  events: {
    details: {
      title: "Event details",
    },
    rsvp: {
      button: {
        attend: "RSVP",
        reject: "I cannot attend",
        full: "Event full",
      },
      success: "Attendance confirmed",
    },
    subtitle: {
      full: "Event full",
      spotsLeft: (count: number) => `${count} spots left`,
      spotsLeftSingular: "1 spot left",
    },
    empty: {
      title: 'No upcoming events',
      description: 'Once events have been posted, they will appear here. To stay updated, enable community update notifications.',
    },
    // Mobile tab navigation
    tabs: {
      upcoming: 'Upcoming',
      past: 'Past',
    },
    tabDescriptions: {
      upcoming: 'View all of the upcoming events',
      past: 'History of past events',
    },
    // Mobile event card buttons
    buttons: {
      willAttend: 'I will attend',
      cantAttend: "I can't attend",
    },
    // Mobile description toggle
    showMore: 'Show more',
    showLess: 'Show less',
    // Empty state for past events
    emptyPast: {
      title: 'No past events',
      description: 'Past events from your community will appear here.',
    },
  },

  // MESSAGES
  messages: {
    title: "Messages",
    empty: {
      title: "No messages",
      description: "Any future messages from your property can be will be here. You can also start a conversation with your property manager.",
    },
    button: {
      create: "New message",
    },
    create: {
      title: "New Conversation",
      cancel: "Cancel",
      subject: {
        placeholder: "Enter subject...",
      },
      content: {
        placeholder: "Your message...",
      },
      info: {
        title: "Have a question?",
        description: "Enter a subject and message to start a conversation with your community manager.",
      },
    },
  },

  // PROFILE
  profile: {
    title: "Profile",
    user: {
      unit: (unit: string) => `Unit #${unit}`,
    },
    option: {
      connections: "Connections",
      profile: "Personal information",
      password: "Change password",
      wifi: "Wifi network",
      roommates: "Roommates",
      howItWorks: "How it works",
      tutorial: "Onboarding tutorial",
      leases: "Leases",
      feedback: "App feedback",
      deleteAccount: "Delete my account",
    },
    deleteAccount: {
      defaultExplanation: "Your explanation is entirely optional...",
      // iOS copy shown above the input on delete screen
      warning: "You will permanently lose all of your events, messages, and profile infomation. This account cannot be recovered.",
      disclaimer: "Resio used data from your property's systems to create your profile. All information within those systems will remain as Resio does not have authorization to remove that information.",
      button: "Delete Account",
    },
    bio: {
      title: "Personal Information",
      button: {
        update: "Update",
        save: "Save",
      },
      placeholder: "Profile Biography",
      crop: "Position and Scale",
    },
    // Post-delete confirmation modal (iOS parity)
    deleteConfirm: {
      title: "Your account will be deleted",
      message: "Typically, Resio will complete this request within 10 business days. After that, you can reestablish your Resio account using the same email address you used for sign up.",
      thanks: "Thank you for using Resio.",
      button: "Finish",
    },
  },

  // WIFI
  wifi: {
    title: "Wifi network",
    qrCode: {
      title: "Scan to connect",
    },
    button: {
      connected: "Connected",
      disconnected: "Connect",
    },
    info: {
      network: "Wireless network name",
      password: "Password",
      unit: "Unit number",
      unitNumber: (unit: string) => `Unit ${unit}`,
    },
    support: {
      title: "Having issues?",
      subtitle: "Contact the CBX support team",
      text: "Text CBX",
      email: "Email CBX",
    },
      noInfo: {
    subtitle: "There appears to be no wifi information available for your unit.\n\nIf this seems incorrect, please contact the CBX support team.",
  },
  },

  // ROOMMATES
  roommates: {
    title: "Roommates",
    apartmentNumber: "Apartment #",
    you: "You",
  },

  // ERRORS
  error: {
    unknown: "There was an issue submitting your request, contact your management office.",
    networkConnection: "There was an issue submitting your request, contact your management office.",
    generic: "Something went wrong. Please try again or contact support",
    invalidCredentials: "Invalid credentials. Please check your email and password.",
    accessDenied: "Access denied. Admin privileges required.",
    serverError: "Something went wrong. Please try again or contact support",
    networkError: "Something went wrong. Please try again or contact support",
    user: {
      notExist: {
        title: "Login failed",
        message: "The provided user information isn't valid.",
      },
    },
    field: {
      invalid: "invalid",
      empty: "required",
      passwordNotMatch: " don't match",
      invalidFormat: "Please check your email and password format",
    },
    api: {
      declinedError: "There was an issue processing your payment, please ensure the payment method is valid and not expired.",
    },
    login: {
      adminFailed: "Admin Login Failed",
      residentFailed: "Login Failed",
      invalidInput: "Invalid Input",
    },
  },

  // COMMON
  common: {
    continue: "Continue",
    closeExit: "Close & Exit",
    logout: "Logout",
    sending: "Sending…",
    loggingOut: "Logging out…",
    phone: {
      call: "Call",
      text: "Text",
    },
  },

  // CONNECTIONS (Legacy - keeping for backward compatibility)
  // Note: Updated connections strings are at the end of this file

  // COMMUNITY RESOURCES
  communityResources: {
    info: {
      title: "INFORMATION",
    },
    website: {
      title: "WEBSITE",
    },
    files: {
      title: "FILES",
    },
    empty: {
      title: "No Community Resources Available",
      description: "Documents, links, and other important resources shared by your community will show here.",
    },
  },

  // USER TYPE
  userType: {
    resident: {
      description: "Resident",
    },
    guest: {
      description: "Guest",
    },
    guarantor: {
      description: "Guarantor",
    },
  },
  linkedAccounts: {
    title: 'Accounts',
    cardTitle: 'Linked Accounts',
    cardDescription: "Invite people and they'll be able to make payments on your behalf",
    cell: {
      sender: 'You can receive payments from this account',
      connectedSinceFormatted: 'Connected %@',
      removeTitle: 'Remove connection',
      capabilityResident: 'This account can pay on your behalf',
      capabilityGuest: 'You can make payments to this account',
      currentLease: 'Current lease:',
      removeConnection: 'Remove Connection',
    },
    confirmDelete: {
      title: 'Remove Connection',
      description: 'Are you sure you want to remove this connection?',
    },
    empty: {
      title: 'No linked accounts',
      description: 'Once you send an invitation to someone and they accept it, your linked accounts will be displayed here.',
    },
    navHeader: {
      linkedAccountsDescription: "View the people you're connected to",
      invitationsDescription: 'Manage your invitations',
    },
  },
  invitations: {
    title: 'Invitations',
    sendInvite: {
      title: 'Send a new invitation',
      description: 'You will need the email address of the person with whom you wish to connect.',
      cta: 'New invitation',
    },
    cell: {
      didInvite: 'You sent this invitation',
      wasInvited: 'Has invited you to connect',
      ctaTitle: 'Accept Invitation',
      cancelTitle: 'Cancel Invitation',
      declineTitle: 'Decline',
      invitedOn: 'Invited on',
      resend: 'Resend',
    },
    confirmCancel: {
      title: 'Cancel Invitation',
      description: 'Are you sure you want to cancel this invitation?',
    },
    confirmDecline: {
      title: 'Decline Invitation', 
      description: 'Are you sure you want to decline this invitation?',
    },
    empty: {
      title: 'No invitations',
      description: 'Sent and received invitations will appear here.',
    },
    list: {
      sectionHeader: 'All Invitations',
      emptyTitle: 'Your inbox is empty',
      emptyDescription: 'All incoming invitation requests will be listed here',
    },
  },
  connections: {
    title: 'Connections',
  },
  newInvitation: {
    title: 'New Invitation',
    modalTitle: 'Send an invitation',
    header: 'Please enter the email address of the person with whom you wish to connect.',
    emailLabel: 'Email',
    emailPlaceholder: 'name@example.com',
    personalMessageLabel: 'Personal Message',
    optionalText: '(optional)',
    messagePlaceholder: 'Enter your personal message...',
    messageFormatted: 'Hi, Let\'s connect on Resio!\n\nWith Resio, you\'ll be able to make payments on my behalf. To get started, simply click the \'Accept invitation\' button.\n\nThanks so much for all your support!\n%@', // %@ will be replaced with user's full name
    buttonTitle: 'Send Invitation',
    cancelButton: 'Cancel',
    sendButton: 'Send',
    disclaimer: 'An email will be sent for their confirmation. Once they have confirmed, they will be able to make a payment to your account. If you are both residents of our partner communities, you will also be able to make payments for each other.',
    loadingText: 'An email has been sent to %@. It may take a few minutes to arrive.',
    validation: {
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      wordLimitExceeded: 'Message exceeds %@ words (%@ words)',
      noSpecialChars: 'No special chars',
    },
    howItWorks: {
      item1: {
        title: 'How it works',
        detail: 'An email will be sent for their confirmation. Once they have confirmed, they will be able to make a payment to your account. If you are both residents of our property, they will also be able to see your roommates.',
      },
      item2: {
        title: 'What information is shared?',
        detail: 'When you invite someone, they will see your outstanding balance, recurring charges, and past payments they have made on your lease. We will not share line item details of your charges or your personal information.',
      },
    },
  },

  // FORGOT
  forgotPassword: {
forgotFieldEmail : "Email",
forgotTitleText : "Forgotten password?",
forgotDescrText : "If you have forgotten your password, enter the email address associated with your account. We'll send you a verification message to reset your password.",
forgotBtnReset : "Next",
  },

  // RESET PASSWORD
  resetPassword: {
resetpwTitle :"Please set a password",
resetpwSubtitle :"A strong password should be at least 12 characters long and should include a mix of uppercase and lowercase letters, numbers, and special characters.",
resetpwFieldPwd :"Password",
resetpwFieldPwdConfirm :"Confirm Password",
resetpwFieldPwdConfirmNew :"Confirm New Password",
resetpwBtnConfirm :"Confirm",
},

// VERIFICATION
verification: {
accountVerifyTitle :"Account verification",
accountVerifyDescription :"Please check your email to verify your account to proceed with setting your password. It might take a few minutes to arrive.",
accountVerifyResend :"Resend email",
accountVerifyCancel :"Go back",
},

AUTOPAY_CHARGES_TOOLTIP: "To view autopay charges, visit this leases's transactions history table in the payments tab",

  // ACCOUNT (My Account desktop)
  account: {
    informationTitle: 'Account Information',
    deleteAccountLink: 'Delete Account',
    deleteAccountConfirmTitle: 'Delete Account',
    deleteAccountConfirmMessage: 'Are you sure you want to delete your account? This cannot be undone.',
    guestAccountName: 'Guest Account',

    resetPasswordTitle: 'Reset Password',
    resetPasswordCopy: 'In order to reset your password, you will need to verify your email. Click ‘Reset password’ to begin.',
    resetPasswordButton: 'Reset Password',
    resetPasswordModalTitle: 'Reset Password',
    resetPasswordModalMessagePrefix: 'We’ve sent a verification email to %@. Please verify your account and proceed with resetting your password. It might take a few minutes to arrive.',
    resetPasswordModalResend: 'Resend',
    resetPasswordModalClose: 'Close',

    notificationsTitle: 'Notification settings',
    notificationsSubtitle: 'Decide what alerts you want to recieve',

    paymentMethodsTitle: 'Payment Methods',
    paymentMethodsEmptyTitle: 'No saved payment methods',
    paymentMethodsEmptyMessage: "You'll need to add a new payment method before making your next payment.",
    paymentMethodsAddTitle: 'Add a new payment method',
    paymentMethodsAddCard: 'Credit/Debit Card',
    paymentMethodsAddACH: 'ACH Bank Transfer',
    paymentMethodsConfirmDeleteTitle: 'Remove payment method',
    paymentMethodsConfirmDeleteMessage: 'Are you sure you want to remove %@?',

    autopayTitle: 'Autopay',
    autopayEmptyTitle: 'No linked accounts',
    autopayEmptyMessage: 'Once you receive and accept an invitation your recipients will be displayed here',
    autopayEditSettings: 'Edit settings',
    autopayRemove: 'Remove',
    autopaySetup: 'Setup Autopay',
    autopayRemovedToast: 'Autopay removed',
    labels: {
      paymentMethod: 'Payment Method',
      autopayAmount: 'Autopay amount',
      firstPayment: 'First payment',
      lastPayment: 'Last payment',
      loading: 'Loading...',
    },
  },

  faqCard: {
    cardTitle: 'You\'ve got questions?',
    cardSubtitle: 'Everything you need to know about the product. Can’t find the answer you’re looking for?',
    cardSubtitle2: 'Contact our support.',
    cardCta: 'Get in touch',
  },

  // MAINTENANCE (iOS parity)
  maintenance: {
    title: 'Service requests', // service.requests.title
    phoneTitle: 'For emergencies, call', // service.requests.phone.title
    hoursTitle: 'Maintenance hours',
    emptyTitle: 'No service requests', // service.requests.empty.title
    emptyDescription: "Once you've submitted a service request, it will be displayed here.", // service.requests.empty.description (approx)
    createButtonTitle: 'Create a service request', // service.requests.create.button.title
    disabledTitle: 'Service requests unavailable', // service.requests.disabled.title
    disabledDescription: "All payments and maintenance requests must be made through your community's ResidentPortal. We apologize for this inconvenience!", // service.requests.disabled.description
    futureDescription: 'Service requests are disabled until you have officially moved in as a resident. Upon activation of your lease, you will be able to create and view service requests.', // service.requests.future.description
  },

  // MOBILE PAYMENTS
  mobilePayments: {
    title: 'Payments',
    tabCurrent: 'Current',
    tabHistory: 'History',
    emptyCurrentTitle: 'No payment information',
    emptyCurrentMessage: 'Your payment information will appear here once your lease is active.',
    emptyHistoryTitle: 'No transactions yet',
    emptyHistoryMessage: 'Once you make a payment, it will appear here.',
    makePaymentButton: 'Make Payment',
    viewDetailsButton: 'View Details',
    paymentSuccessTitle: 'Payment Successful',
    paymentSuccessMessage: 'Your payment has been processed successfully.',
    paymentFailedTitle: 'Payment Failed',
    paymentFailedMessage: 'We were unable to process your payment. Please try again.',
    balanceDue: 'Balance due',
    noDue: 'No balance due',
    credit: 'Credit',
    pastDue: 'Past Due',
    addPaymentMethod: 'Add Payment Method',
    selectPaymentMethod: 'Select Payment Method',
    enterAmount: 'Enter Amount',
    reviewPayment: 'Review Payment',
    confirmPayment: 'Confirm Payment',
    processing: 'Processing...',
    setupAutopay: 'Setup Autopay',
    manageAutopay: 'Manage Autopay',
    autopayActive: 'Autopay Active',
    leaseDetails: 'Lease Details',
    scheduledCharges: 'Scheduled Charges',
    transactionHistory: 'Transaction History',
    paymentMethods: 'Payment Methods',
    cardEndingIn: 'Card ending in',
    bankAccountEndingIn: 'Account ending in',
    defaultPaymentMethod: 'Default',
    setAsDefault: 'Set as default',
    removePaymentMethod: 'Remove',
    addCreditCard: 'Add Credit/Debit Card',
    addBankAccount: 'Add Bank Account',
    cardNumber: 'Card Number',
    expiryDate: 'Expiry Date',
    cvv: 'CVV',
    accountNumber: 'Account Number',
    routingNumber: 'Routing Number',
    accountType: 'Account Type',
    checking: 'Checking',
    savings: 'Savings',
    nameOnCard: 'Name on Card',
    nameOnAccount: 'Name on Account',
    billingZip: 'Billing ZIP Code',
    savePaymentMethod: 'Save Payment Method',
    autopayAmountLabel: 'Payment Amount',
    autopayFullBalance: 'Full Balance',
    autopayFixedAmount: 'Fixed Amount',
    autopayStartDate: 'Start Date',
    autopayPaymentMethod: 'Payment Method',
    autopayConfirmSetup: 'Confirm Autopay Setup',
    autopayCancelConfirmTitle: 'Cancel Autopay',
    autopayCancelConfirmMessage: 'Are you sure you want to cancel autopay for this lease?',
    autopaySetupSuccess: 'Autopay has been set up successfully',
    autopayCancelSuccess: 'Autopay has been cancelled',
    feeNote: 'Processing fees will be added',
    totalWithFees: 'Total (including fees)',
    paymentBlocked: 'Payments are currently unavailable',
    paymentsBlockedMessage: 'No payments are being accepted at this time. Please contact your community manager if you have questions.',
    // V2 Design Strings
    currentLease: 'Current lease',
    myAccount: 'My account',
    seeDetails: 'See details',
    showRecurringCharges: 'Show recurring charges',
    backToAllLeases: 'Back to all leases',
    youHaveLeases: (count: number) => `You have ${count} leases`,
    // Balance States
    balanceOwedMessage: 'This account has outstanding charges. Payments are due by the 1st of each month.',
    balanceZeroMessage: 'Great! This account has no outstanding charges.',
    balanceCreditMessage: 'This account has a credit',
    balanceOverdueMessage: 'This account has overdue charges. Payments are due by the 1st of each month.',
    paymentsNotAccepted: 'No payments are being accepted at this time. Please contact your community manager if you have questions.',
    // Info Bubble (matches Figma designs)
    infoBubbleOwedTitle: 'Balance due',
    infoBubbleOwedDesc: 'The balance includes both the rent and utility charges, if there are any.',
    infoBubbleZeroTitle: 'No balance due',
    infoBubbleZeroDesc: 'You can always pay ahead to cover future charges.',
    infoBubbleCreditTitle: 'Account credit',
    infoBubbleCreditDesc: 'The credit will be applied automatically to fully or partially cover any outstanding charges that you have.',
    infoBubblePastDueTitle: 'Account past due',
    infoBubblePastDueDesc: 'The late fees for this account will continue to occur until the balance gets paid.',
    // Primary Leaseholder Tooltip
    primaryLeaseholderTooltip: 'You are the primary leaseholder',
    // Transaction History
    emptyTransactionHistory: 'As soon as you or someone on your behalf makes a first transaction, it will appear here.',
    sortBy: 'Sort by',
    description: 'DESCRIPTION',
    posted: 'POSTED',
    amount: 'AMOUNT',
    // Pagination
    first: 'First',
    last: 'Last',
    pageOf: (current: number, total: number) => `${current} of ${total}`,
    // Recurring Charges
    recurringCharges: 'Recurring charges',
    monthly: 'Monthly',
    tbd: 'TBD',
    // Autopay Details Modal
    autopayFor: (name: string) => `Autopay for ${name}`,
    autopayAmount: 'Autopay amount',
    autopayStart: 'Autopay start',
    autopayEnd: 'Autopay end',
    processed: 'Processed',
    editSettings: 'Edit settings',
    remove: 'Remove',
    done: 'Done',
    // Remove Autopay Modal
    removeAutopayConfirm: 'Are you sure you want to remove autopay for your account?',
    noCancel: 'No, cancel',
    proceed: 'Proceed',
    // Autopay Status
    autopaySetup: 'Autopay setup',
    autopayRemovedSuccess: 'Autopay removed successfully',
    autopayUpdatedSuccess: 'Autopay settings updated successfully',
    pastDueBalance: 'Past due balance!',
    makeAPayment: 'Make a payment',
    // Pending Payments
    paymentsPending: 'Payments pending',
    paymentsPendingMessage: 'Your balance will be updated shortly. Please note that sometimes it can take up to 30 minutes.',
    paymentsDeclinedMessage: 'Please check with your card issuer. If the problem persists, please',
    contactUs: 'contact us.',
    // Transaction Status
    statusPending: 'Pending',
    statusSuccessful: 'Successful',
    statusDeclined: 'Declined',
    // Transaction Details Modal
    transactionDetails: 'Transaction details',
    status: 'STATUS',
    paymentId: 'PAYMENT ID',
    paymentMethod: 'PAYMENT METHOD',
    paymentAmount: 'PAYMENT AMOUNT',
    convenienceFee: 'CONVENIENCE FEE',
    totalCharges: 'TOTAL CHARGES',
  },
} as const;

// Helper function for dynamic strings with parameters
export const formatString = (template: string, ...args: any[]): string => {
  return template.replace(/%@/g, () => args.shift()?.toString() || '');
};

// Export specific sections for easier imports
export const homeStrings = strings.home;
export const notificationStrings = strings.notifications;
export const loginStrings = strings.login;
export const errorStrings = strings.error;
export const profileStrings = strings.profile;
export const commonStrings = strings.common;
export const eventStrings = strings.events;
export const linkedAccountsStrings = strings.linkedAccounts;
export const invitationsStrings = strings.invitations;
export const connectionsStrings = strings.connections;
export const newInvitationStrings = strings.newInvitation;
export const logoutStrings = strings.logout;
export const mobilePaymentsStrings = strings.mobilePayments;